'use client';

import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import { localizeUploadError } from '@/shared/lib/upload-error';
import {
  FileUploadDropZone,
  type FileUploadDropZoneRef,
} from '@/shared/ui/FileUploadDropZone';
import {
  AngleUpload,
  type AngleUploadRef,
  type ValidationItem,
} from '@/features/angle';
import {
  ProjectReferenceUpload,
  type ProjectReferenceUploadRef,
} from '@/features/project-reference/ui/ProjectReferenceUpload';
import { useProjectReferences } from '@/features/project-reference/hooks/use-project-references';
import { useUploadStore } from '@/features/upload';
import {
  usePaywallPayload,
  usePaywallPayloads,
} from '@/features/subscription/model/subscription.selectors';
import { useLimitPaywallStore } from '@/shared/stores/limit-paywall-store';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import {
  isDocumentReference,
  isImageReference,
  isSystemReference,
} from '@/types/project-reference';
import { useAnglesOverview } from '../hooks/use-angles-overview';

// Mixed accept — images (routed to angle validation, drawings auto-route to
// documents server-side) plus the document types the Brief documents accept.
export const BRIEF_INTAKE_ACCEPT =
  'image/*,application/pdf,text/plain,text/markdown,.md';

export interface BriefIntakeRef {
  /** Open the mixed (photos + documents) native file dialog. */
  openFileDialog: () => void;
  /** Route already-picked files (file dialog, drag & drop) into the intake. */
  processFiles: (files: File[]) => void;
}

interface BriefIntakeProps {
  projectId: string;
  // Called when the first photos land in a previously empty project, so the
  // page-level "Create design" flow can proactively open prefilled with them.
  onFirstUpload?: (angleIds: string[]) => void;
}

/**
 * The Brief's unified file intake, headless. Renders nothing visible: the
 * hidden mixed file dialog, the AngleUpload pipeline (crop → upload →
 * validation modal) for images and the Brief-document reference upload for
 * everything else. Drives itself through the ref, so any surface (the Brief
 * page grid, a page-wide drop target, the building page's Brief section) can
 * feed it files and get identical routing, limits and reconciliation.
 */
export const BriefIntake = forwardRef<BriefIntakeRef, BriefIntakeProps>(
  function BriefIntake({ projectId, onFirstUpload }, ref) {
    const router = useRouter();
    const queryClient = useQueryClient();
    const tUpload = useTranslations('fileUpload');

    const { angles, angleCount, refetch } = useAnglesOverview(projectId);

    // Brief-document counts for the reference-limit pre-flight (same bucket the
    // documents land in). System references never count against the user.
    const { data: briefReferences = [] } = useProjectReferences(projectId, {
      section: 'brief',
    });
    const { imageReferenceCount, documentReferenceCount } = useMemo(() => {
      const user = briefReferences.filter((r) => !isSystemReference(r));
      return {
        imageReferenceCount: user.filter(isImageReference).length,
        documentReferenceCount: user.filter(isDocumentReference).length,
      };
    }, [briefReferences]);

    // ---- Paywalls ---------------------------------------------------------
    const angleLibraryPayload = usePaywallPayload(
      'ANGLE_LIBRARY_LIMIT_EXCEEDED'
    );
    const referencePayloads = usePaywallPayloads();
    const showPaywall = useLimitPaywallStore((s) => s.showPaywall);

    const handleLibraryLimitReached = useCallback(() => {
      if (angleLibraryPayload) {
        showPaywall(angleLibraryPayload, 'ANGLE_LIBRARY_LIMIT_EXCEEDED');
      } else {
        router.push('/account/tokens');
      }
    }, [angleLibraryPayload, showPaywall, router]);

    // ---- Refs into the two pipelines --------------------------------------
    const dropZoneRef = useRef<FileUploadDropZoneRef>(null);
    const angleUploadRef = useRef<AngleUploadRef>(null);
    const referenceUploadRef = useRef<ProjectReferenceUploadRef>(null);

    // Documents (PDF/txt/md) picked in a MIXED batch (alongside images). They
    // upload via the reference flow below but are mirrored as review cards
    // inside the image upload modal so they aren't silently filed. We hold the
    // original File objects and resolve each one's live status from the upload
    // store by File identity (===) — robust across the queue/batch/completed/
    // failed lists. Cleared when the modal flow ends. Empty for image-only /
    // docs-only batches.
    const [reviewDocs, setReviewDocs] = useState<{ id: string; file: File }[]>(
      []
    );

    const uploadQueue = useUploadStore((s) => s.queue);
    const uploadBatches = useUploadStore((s) => s.batches);
    const completedUploads = useUploadStore((s) => s.completedUploads);
    const failedUploads = useUploadStore((s) => s.failedUploads);

    // Mixed-batch document review rows for the image upload modal, mapping the
    // store status to the modal's neutral lifecycle: uploading →
    // moved_to_documents ("Classified as document") → error. No validation, no
    // thumbnail.
    const documentReviewItems = useMemo<ValidationItem[]>(() => {
      if (reviewDocs.length === 0) return [];
      const all = [
        ...uploadQueue,
        ...Object.values(uploadBatches).flatMap((b) => b.items),
        ...completedUploads,
        ...failedUploads,
      ];
      return reviewDocs.map(({ id, file }) => {
        const item = all.find((it) => it.file === file);
        const status: ValidationItem['status'] =
          item?.status === 'completed'
            ? 'moved_to_documents'
            : item?.status === 'failed' || item?.status === 'cancelled'
              ? 'error'
              : 'uploading';
        return {
          id,
          kind: 'document',
          fileName: file.name,
          status,
          // Localized here, never in the store: what was thrown is a raw
          // value (an S3 XML body among them), and it was reaching this card
          // verbatim in all 24 locales.
          error:
            item?.error !== undefined
              ? localizeUploadError(item.error, tUpload, tUpload('uploadFailedRetry'))
              : undefined,
        };
      });
    }, [reviewDocs, uploadQueue, uploadBatches, completedUploads, failedUploads]);

    // ---- Intake: partition a mixed selection ------------------------------
    const processFiles = useCallback((files: File[]) => {
      if (files.length === 0) return;
      // Partition by MIME: images → angle validation (auto-routes drawings to
      // documents server-side); everything else → Brief document.
      const images = files.filter((f) => f.type.startsWith('image/'));
      const documents = files.filter((f) => !f.type.startsWith('image/'));
      // MIXED batch only: when there are also images, mirror the documents as
      // review cards inside the image upload modal so they don't vanish. A
      // docs-only batch keeps the current behaviour (optimistic grid
      // placeholder, no modal), so we clear any stale review docs in that case.
      setReviewDocs(
        images.length > 0 && documents.length > 0
          ? documents.map((file) => ({
              id: `reviewdoc-${file.name}-${file.size}-${file.lastModified}`,
              file,
            }))
          : []
      );
      if (images.length > 0) angleUploadRef.current?.processFiles(images);
      if (documents.length > 0) {
        referenceUploadRef.current?.processFiles(documents);
      }
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        openFileDialog: () => dropZoneRef.current?.openFileDialog(),
        processFiles,
      }),
      [processFiles]
    );

    const invalidateReferences = useCallback(
      () =>
        queryClient.invalidateQueries({
          queryKey: cacheKeys.references.listsByProject(projectId),
        }),
      [queryClient, projectId]
    );

    // Hidden uploaders — the drop zone only serves as the mixed file dialog;
    // AngleUpload renders its own validation modal (per-image outcome).
    return (
      <div className="hidden">
        <FileUploadDropZone
          ref={dropZoneRef}
          onFilesSelected={processFiles}
          accept={BRIEF_INTAKE_ACCEPT}
          multiple
        />
        <AngleUpload
          ref={angleUploadRef}
          projectId={projectId}
          allowMultiple
          showValidation
          documentItems={documentReviewItems}
          currentProjectAngleLibraryCount={angleCount}
          onProjectAngleLibraryLimitReached={handleLibraryLimitReached}
          onSuccess={async (response) => {
            // Modal flow finished — drop the mirrored document review rows (the
            // real docs already reconcile into the grid via their own upload).
            setReviewDocs([]);
            const wasFirstUpload = angles.length === 0;
            await queryClient.invalidateQueries({
              queryKey: cacheKeys.projectAngles.list(projectId),
            });
            // Deterministic reconcile for auto-routed materials: an uploaded
            // image classified as a technical drawing/document, inspiration, or
            // output-style ref is turned into a project_reference (section brief
            // or reference) and its angle is deleted. That is invisible to the
            // angle-list invalidation, and the WS references/created push can be
            // dropped, so the routed tile would never appear. Sweep every
            // section cache off completion (API is source of truth).
            await invalidateReferences();
            refetch();
            const responses = Array.isArray(response) ? response : [response];
            const ids = responses
              .map((r) => r.id)
              .filter((id): id is string => !!id);
            if (wasFirstUpload && ids.length > 0) {
              onFirstUpload?.(ids);
            }
          }}
          onCancel={() => setReviewDocs([])}
          onError={(err) => {
            setReviewDocs([]);
            console.error('Brief intake (images) failed:', err);
          }}
        />
        <ProjectReferenceUpload
          ref={referenceUploadRef}
          projectId={projectId}
          section="brief"
          acceptPdf
          onSuccess={invalidateReferences}
          currentImageReferenceCount={imageReferenceCount}
          currentDocumentReferenceCount={documentReferenceCount}
          onLimitReached={(category) => {
            const payload =
              referencePayloads?.[
                `PROJECT_REFERENCE_LIMIT_EXCEEDED:${category}`
              ];
            if (payload) {
              showPaywall(payload, 'PROJECT_REFERENCE_LIMIT_EXCEEDED');
            }
          }}
        />
      </div>
    );
  }
);
